import { Badge } from '@workspace/ui/components/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import { ChevronRight } from 'lucide-react'
import { useAdminInvestorThesisQuery } from '../hooks/use-admin-api'

type Member = NonNullable<
  ReturnType<typeof useAdminInvestorThesisQuery>['data']
>['data']['members'][number]
type ThesisBody = Member['thesis']

const formatDate = (value: string | null) => value ?? '-'

function ThesisBodyView({ thesis }: { thesis: ThesisBody }) {
  const mustHaves = Object.entries(thesis.mustHaves)
  return (
    <div className="space-y-3 text-sm">
      {mustHaves.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
          {mustHaves.map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-muted-foreground">{key.replaceAll('_', ' ')}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {thesis.nevers.length > 0 && (
        <p>
          <span className="text-muted-foreground">Never: </span>
          {thesis.nevers.join(', ')}
        </p>
      )}
      {thesis.brief && <p className="whitespace-pre-line">{thesis.brief}</p>}
      {mustHaves.length === 0 && thesis.nevers.length === 0 && !thesis.brief && (
        <p className="text-muted-foreground">Empty.</p>
      )}
    </div>
  )
}

/** The thesis each member of the firm set through their AI assistant (the thesis tool), with earlier versions. */
export function InvestorThesisSection({ investorId }: { investorId: string }) {
  const query = useAdminInvestorThesisQuery(investorId)
  const members = query.data?.data.members ?? []

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Thesis set through the AI assistant</h3>
        <p className="text-xs text-muted-foreground">
          What each member wrote with the thesis tool. Earlier versions stay below the current one.
        </p>
      </div>
      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No member has set a thesis through their AI assistant.
        </p>
      ) : (
        members.map((member) => (
          <div
            key={member.userId}
            className="space-y-3 rounded-lg border border-border bg-card p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium">{member.name ?? member.email}</span>
              <Badge variant="secondary" size="sm">
                Version {member.version}
              </Badge>
            </div>
            <ThesisBodyView thesis={member.thesis} />
            {member.history.length > 0 && (
              <Collapsible className="rounded-lg border border-border">
                <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground">
                  <ChevronRight className="size-4 transition-transform [[data-state=open]>&]:rotate-90" />
                  {member.history.length} earlier{' '}
                  {member.history.length === 1 ? 'version' : 'versions'}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-4 border-t border-border p-3">
                    {[...member.history].reverse().map((entry) => (
                      <div key={`${entry.version}-${entry.replacedOn}`} className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Version {entry.version ?? '?'}, replaced {formatDate(entry.replacedOn)}
                          {entry.changedBy ? `, by ${entry.changedBy}` : ''}
                        </p>
                        <ThesisBodyView thesis={entry.thesis} />
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        ))
      )}
    </section>
  )
}
